import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PiggyBank, Loader2, Undo2 } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";

const MES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

export const CaixaInvestCard = ({ onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const load = () => api.get("/finance/caixa/investimento").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d || (!d.pct && !d.total_separated)) return null;
  const nowMonth = new Date().toISOString().slice(0, 7);
  const cur = d.months.find((m) => m.month === nowMonth) || { notes: 0, caixa: 0, invest: 0 };
  const baixa = async (source) => {
    setAsk(false);
    setBusy(true);
    try { await api.post("/finance/caixa/investimento/baixa", { source }); toast.success("Baixa registrada"); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro na baixa"); }
    finally { setBusy(false); }
  };
  const undo = async (b) => {
    if (!window.confirm(`Desfazer esta baixa? O valor volta para o ${b.source === "conta_dia" ? "Dia a Dia" : "Caixa"}.`)) return;
    await api.delete(`/finance/caixa/investimento/${b.id}`).then(() => { load(); onChanged?.(); }).catch(() => toast.error("Erro ao desfazer"));
  };
  return (
    <section className="bg-white rounded-2xl border border-violet-200 p-5 card-shadow" data-testid="caixa-invest-card">
      <div className="flex items-center gap-2 mb-2">
        <PiggyBank className="w-4 h-4 text-violet-700" />
        <h2 className="font-heading text-base font-black text-slate-900 flex-1">Investimento Rafael ({d.pct}% do caixa)</h2>
        <Link to="/financeiro/investimento-rafael" className="text-xs font-black text-violet-700 hover:underline" data-testid="caixa-invest-extrato-link">Ver extrato</Link>
      </div>
      <p className="text-sm text-slate-700" data-testid="caixa-invest-month">
        Em {MES[Number(nowMonth.slice(5, 7)) - 1]} foram <b>{BRL(cur.caixa)}</b> para o Caixa de <b>{cur.notes}</b> nota(s), com <b>{BRL(cur.invest)}</b> separados para investir.
      </p>
      <div className="mt-3 rounded-xl bg-violet-50 border border-violet-200 p-3 flex items-center gap-3">
        <div className="flex-1">
          <p className="text-[10px] font-black uppercase tracking-widest text-violet-700">Disponível para investir</p>
          <p className="text-xl font-black tabular-nums text-violet-800" data-testid="caixa-invest-available">{BRL(d.available)}</p>
          <p className="text-[10.5px] text-violet-700/80" data-testid="caixa-invest-rule">
            {d.baixa_mode === "mes_anterior" ? `Baixa do mês anterior esquecida: ${BRL(d.baixa_amount)} (o do mês atual acumula até ${formatDateBR(d.baixa_day)})` : d.baixa_mode === "mes_atual" ? `Hoje é o último dia do mês: dê baixa de ${BRL(d.baixa_amount)}` : d.baixa_done_this_month ? "Baixa deste mês já feita — próxima no último dia do mês que vem" : `Baixa única, do valor total, em ${formatDateBR(d.baixa_day)} (último dia do mês)`}
          </p>
        </div>
        <Button onClick={() => setAsk((v) => !v)} disabled={busy || d.available <= 0 || !d.can_baixa} className="bg-violet-600 hover:bg-violet-700 text-white font-black" data-testid="caixa-invest-baixa">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Dar baixa"}
        </Button>
      </div>
      {ask && d.can_baixa && (
        <div className="mt-2 rounded-xl border border-violet-200 p-3 space-y-2" data-testid="caixa-invest-source">
          <p className="text-xs font-bold text-slate-700">De qual conta saiu o dinheiro dos {BRL(d.baixa_amount)}?</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => baixa("caixa")} data-testid="caixa-invest-source-caixa" className="h-auto py-2 flex-col">
              <b>Do Caixa</b><span className="text-[10px] text-slate-500 font-normal">sai do saldo do Caixa</span>
            </Button>
            <Button variant="outline" onClick={() => baixa("conta_dia")} data-testid="caixa-invest-source-contadia" className="h-auto py-2 flex-col">
              <b>Do Dia a Dia</b><span className="text-[10px] text-slate-500 font-normal">sai do Dia a Dia; Caixa fica igual</span>
            </Button>
          </div>
        </div>
      )}
      {d.baixas.length > 0 && (
        <ul className="divide-y divide-slate-100 mt-3" data-testid="caixa-invest-history">
          {d.baixas.map((b) => (
            <li key={b.id} className="py-2 flex items-center gap-2 text-xs">
              <span className="text-slate-500 w-20">{formatDateBR(b.date)}</span>
              <span className="flex-1 text-slate-700 truncate">Baixa {b.source === "conta_dia" ? "pelo Dia a Dia" : "pelo Caixa"}{b.note ? ` — ${b.note}` : ""} • {b.created_by}</span>
              <b className="tabular-nums text-violet-700">− {BRL(b.amount)}</b>
              <button onClick={() => undo(b)} className="text-slate-300 hover:text-rose-600" title="Desfazer" data-testid={`caixa-invest-undo-${b.id}`}><Undo2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
